import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Eye, Edit3, LogIn, Image, Lock, Shield, Trash2,
  RefreshCw, Settings, MessageSquare, Activity,
  BarChart3, TrendingUp, Users, Clock, X, Loader2,
  AlertTriangle, CheckCircle, Key, RotateCcw
} from 'lucide-react'
import { buildApiUrl } from '../config'
import { Button, Card, Input, Alert, Badge } from './ui'

interface ActivityLog {
  handle: string
  timestamp: string
  action: 'message_view' | 'dashboard_login' | 'message_update' | 'image_upload' |
           'password_change' | 'admin_password_reset' | 'admin_message_deletion' | 'admin_message_reset'
  userAgent?: string
  ipAddress?: string
  referrer?: string
  metadata?: Record<string, any>
}

interface OverviewStats {
  totalMessages: number
  totalViews: number
  totalUpdates: number
  totalLogins: number
  topHandles: Array<{ handle: string; views: number }>
  recentActivity: ActivityLog[]
}

const ACTION_LABELS: Record<string, string> = {
  message_view: 'View',
  dashboard_login: 'Login',
  message_update: 'Update',
  image_upload: 'Image',
  password_change: 'Password',
  admin_password_reset: 'Admin Reset',
  admin_message_deletion: 'Deletion',
  admin_message_reset: 'Msg Reset',
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  message_view: <Eye size={12} />,
  dashboard_login: <LogIn size={12} />,
  message_update: <Edit3 size={12} />,
  image_upload: <Image size={12} />,
  password_change: <Lock size={12} />,
  admin_password_reset: <Key size={12} />,
  admin_message_deletion: <Trash2 size={12} />,
  admin_message_reset: <RotateCcw size={12} />,
}

const ACTION_BADGE_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  message_view: 'default',
  dashboard_login: 'success',
  message_update: 'warning',
  image_upload: 'info',
  password_change: 'warning',
  admin_password_reset: 'danger',
  admin_message_deletion: 'danger',
  admin_message_reset: 'danger',
}

const getAdminPassword = () => localStorage.getItem('adminPassword') || ''

const AdminNew = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(false)

  const [activeTab, setActiveTab] = useState<'overview' | 'activity' | 'messages' | 'config'>('overview')
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Overview
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null)

  // Activity
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [filters, setFilters] = useState({ action: '', handle: '', limit: 200 })

  // Messages
  const [messages, setMessages] = useState<any[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messageFilters, setMessageFilters] = useState({ handle: '', search: '', limit: 100 })
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  // Config
  const [config, setConfig] = useState({
    maxImageFileSizeMB: 5,
    maxImageWidth: 1200,
    maxImageHeight: 1200,
    imageQuality: 0.8
  })
  const [configLoading, setConfigLoading] = useState(false)
  const [updatingConfig, setUpdatingConfig] = useState(false)
  const [resetPasswordHandle, setResetPasswordHandle] = useState('')
  const [resettingPassword, setResettingPassword] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem('adminPassword')
    if (saved) {
      setIsAuthenticated(true)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return
    if (activeTab === 'overview') fetchOverviewData()
    else if (activeTab === 'activity') fetchActivityLogs()
    else if (activeTab === 'messages') fetchMessages()
    else if (activeTab === 'config') fetchConfig()
  }, [activeTab, isAuthenticated])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthLoading(true)
    setAuthError(null)
    try {
      const response = await fetch(buildApiUrl('/admin/activity'), {
        headers: { 'X-Admin-Password': password }
      })
      if (response.ok) {
        localStorage.setItem('adminPassword', password)
        setIsAuthenticated(true)
      } else {
        setAuthError('Invalid admin password')
      }
    } catch {
      setAuthError('Network error — check connection')
    } finally {
      setAuthLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('adminPassword')
    setIsAuthenticated(false)
    setPassword('')
    setActivityLogs([])
    setMessages([])
    setOverviewStats(null)
    setError(null)
    setSuccessMessage(null)
  }

  const fetchOverviewData = async () => {
    setOverviewLoading(true)
    setError(null)
    try {
      const pwd = getAdminPassword()
      const [msgRes, actRes] = await Promise.all([
        fetch(buildApiUrl('/admin/messages?limit=500'), {
          headers: { 'X-Admin-Password': pwd }
        }),
        fetch(buildApiUrl('/admin/activity?limit=500'), {
          headers: { 'X-Admin-Password': pwd }
        })
      ])

      if (!msgRes.ok || !actRes.ok) {
        throw new Error('Failed to fetch overview data')
      }

      const msgData = await msgRes.json()
      const actData = await actRes.json()

      const logs: ActivityLog[] = actData.activityLogs || []
      const msgs: any[] = msgData.messages || []

      const views = logs.filter(l => l.action === 'message_view')
      const updates = logs.filter(l => l.action === 'message_update')
      const logins = logs.filter(l => l.action === 'dashboard_login')

      // Count views per handle
      const viewsByHandle: Record<string, number> = {}
      views.forEach(l => {
        viewsByHandle[l.handle] = (viewsByHandle[l.handle] || 0) + 1
      })
      const topHandles = Object.entries(viewsByHandle)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([handle, views]) => ({ handle, views }))

      // Recent activity (last 10, already sorted by timestamp desc)
      const recentActivity = logs.slice(0, 10)

      setOverviewStats({
        totalMessages: msgs.length,
        totalViews: views.length,
        totalUpdates: updates.length,
        totalLogins: logins.length,
        topHandles,
        recentActivity,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setOverviewLoading(false)
    }
  }

  const fetchActivityLogs = async () => {
    setActivityLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (filters.action) params.append('action', filters.action)
      if (filters.handle) params.append('handle', filters.handle)
      params.append('limit', filters.limit.toString())

      const response = await fetch(buildApiUrl(`/admin/activity?${params}`), {
        headers: { 'X-Admin-Password': getAdminPassword() }
      })
      if (!response.ok) {
        if (response.status === 401) { setError('Admin auth failed'); return }
        throw new Error('Failed to fetch activity logs')
      }
      const data = await response.json()
      setActivityLogs(data.activityLogs || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setActivityLoading(false)
    }
  }

  const fetchMessages = async () => {
    setMessagesLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (messageFilters.handle) params.append('handle', messageFilters.handle)
      if (messageFilters.search) params.append('search', messageFilters.search)
      params.append('limit', messageFilters.limit.toString())

      const response = await fetch(buildApiUrl(`/admin/messages?${params}`), {
        headers: { 'X-Admin-Password': getAdminPassword() }
      })
      if (!response.ok) {
        if (response.status === 401) { setError('Admin auth failed'); return }
        throw new Error('Failed to fetch messages')
      }
      const data = await response.json()
      setMessages(data.messages || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setMessagesLoading(false)
    }
  }

  const handleDeleteMessage = async (handle: string) => {
    if (deleteConfirm !== handle) {
      setDeleteConfirm(handle)
      return
    }
    setDeleting(handle)
    setError(null)
    try {
      const response = await fetch(buildApiUrl(`/admin/delete-message/${handle}`), {
        method: 'DELETE',
        headers: { 'X-Admin-Password': getAdminPassword() }
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to delete message')
      }
      setMessages(prev => prev.filter(m => m.handle !== handle))
      setSuccessMessage(`Deleted @${handle} successfully`)
      setDeleteConfirm(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setDeleting(null)
    }
  }

  const fetchConfig = async () => {
    setConfigLoading(true)
    setError(null)
    try {
      const response = await fetch(buildApiUrl('/admin/config'), {
        headers: { 'X-Admin-Password': getAdminPassword() }
      })
      if (!response.ok) throw new Error('Failed to fetch config')
      const data = await response.json()
      setConfig(data.config)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setConfigLoading(false)
    }
  }

  const updateConfig = async () => {
    setUpdatingConfig(true)
    setError(null)
    try {
      const response = await fetch(buildApiUrl('/admin/config'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': getAdminPassword()
        },
        body: JSON.stringify(config)
      })
      if (!response.ok) throw new Error('Failed to update config')
      const data = await response.json()
      setConfig(data.config)
      setSuccessMessage('Configuration saved')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setUpdatingConfig(false)
    }
  }

  const handleResetPassword = async () => {
    if (!resetPasswordHandle.trim()) return
    setResettingPassword(true)
    setError(null)
    try {
      const response = await fetch(buildApiUrl(`/admin/reset-password/${resetPasswordHandle}`), {
        method: 'POST',
        headers: { 'X-Admin-Password': getAdminPassword() }
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to reset password')
      }
      setSuccessMessage(`Password reset for @${resetPasswordHandle}`)
      setResetPasswordHandle('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setResettingPassword(false)
    }
  }

  const formatTime = (ts: string) => {
    const d = new Date(ts)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString()
  }

  const ActionBadge = ({ action }: { action: string }) => (
    <Badge variant={ACTION_BADGE_VARIANT[action] || 'default'} className="flex items-center gap-1">
      {ACTION_ICONS[action]}
      {ACTION_LABELS[action] || action}
    </Badge>
  )

  // ─── Login screen ───────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <>
        <Helmet><title>Admin - YouWont</title></Helmet>
        <div className="min-h-screen flex items-center justify-center py-12 px-4">
          <Card className="max-w-sm w-full">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand/10 mb-4">
                <Shield size={28} className="text-brand" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Admin Access</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Enter your admin password</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                label="Password"
                type="password"
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {authError && <Alert variant="danger">{authError}</Alert>}
              <Button type="submit" variant="primary" size="lg" disabled={authLoading} className="w-full">
                {authLoading ? <><Loader2 size={16} className="mr-2 animate-spin" />Checking...</> : 'Sign In'}
              </Button>
            </form>
          </Card>
        </div>
      </>
    )
  }

  // ─── Tabs config ─────────────────────────────────────────────────────
  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={15} /> },
    { id: 'activity', label: 'Activity', icon: <Activity size={15} /> },
    { id: 'messages', label: 'Messages', icon: <MessageSquare size={15} /> },
    { id: 'config', label: 'Config', icon: <Settings size={15} /> },
  ] as const

  return (
    <>
      <Helmet><title>Admin — YouWont</title></Helmet>

      <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Admin</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">YouWont management console</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="mt-3 sm:mt-0 self-start sm:self-auto">
              <X size={14} className="mr-1.5" />
              Sign out
            </Button>
          </div>

          {/* Global alerts */}
          {error && (
            <Alert variant="danger" className="mb-5">
              <div className="flex items-center justify-between gap-2">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="shrink-0 opacity-60 hover:opacity-100">
                  <X size={14} />
                </button>
              </div>
            </Alert>
          )}
          {successMessage && (
            <Alert variant="success" className="mb-5">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2"><CheckCircle size={14} />{successMessage}</span>
                <button onClick={() => setSuccessMessage(null)} className="shrink-0 opacity-60 hover:opacity-100">
                  <X size={14} />
                </button>
              </div>
            </Alert>
          )}

          {/* Tab bar */}
          <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mb-8 w-fit">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-slate-900 text-brand shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Overview Tab ── */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Usage Overview</h2>
                <Button variant="ghost" size="sm" onClick={fetchOverviewData} disabled={overviewLoading}>
                  <RefreshCw size={14} className={`mr-1.5 ${overviewLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {overviewLoading && !overviewStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
                      <div className="h-8 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                    </Card>
                  ))}
                </div>
              )}

              {overviewStats && (
                <>
                  {/* Stat cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card className="!p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-brand/10 rounded-lg">
                          <Users size={16} className="text-brand" />
                        </div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">QR Codes</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{overviewStats.totalMessages}</p>
                    </Card>

                    <Card className="!p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-blue-500/10 rounded-lg">
                          <Eye size={16} className="text-blue-500" />
                        </div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Views</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{overviewStats.totalViews}</p>
                    </Card>

                    <Card className="!p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-amber-500/10 rounded-lg">
                          <Edit3 size={16} className="text-amber-500" />
                        </div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Updates</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{overviewStats.totalUpdates}</p>
                    </Card>

                    <Card className="!p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-green-500/10 rounded-lg">
                          <LogIn size={16} className="text-green-500" />
                        </div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Logins</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{overviewStats.totalLogins}</p>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Handles */}
                    <Card>
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp size={16} className="text-brand" />
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Top Handles by Views</h3>
                      </div>
                      {overviewStats.topHandles.length === 0 ? (
                        <p className="text-slate-400 dark:text-slate-500 text-sm py-4 text-center">No view data yet</p>
                      ) : (
                        <div className="space-y-3">
                          {overviewStats.topHandles.map(({ handle, views }, i) => {
                            const maxViews = overviewStats.topHandles[0]?.views || 1
                            const pct = Math.round((views / maxViews) * 100)
                            return (
                              <div key={handle}>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-400 w-4">#{i + 1}</span>
                                    <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-200">@{handle}</span>
                                  </div>
                                  <span className="text-sm font-bold text-brand">{views}</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-brand to-purple-500 rounded-full transition-all"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </Card>

                    {/* Recent Activity */}
                    <Card>
                      <div className="flex items-center gap-2 mb-4">
                        <Clock size={16} className="text-brand" />
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Recent Activity</h3>
                      </div>
                      {overviewStats.recentActivity.length === 0 ? (
                        <p className="text-slate-400 dark:text-slate-500 text-sm py-4 text-center">No activity yet</p>
                      ) : (
                        <div className="space-y-2">
                          {overviewStats.recentActivity.map((log, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <ActionBadge action={log.action} />
                                <span className="font-mono text-xs text-slate-600 dark:text-slate-400 truncate">@{log.handle}</span>
                              </div>
                              <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{formatTime(log.timestamp)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Activity Tab ── */}
          {activeTab === 'activity' && (
            <div className="space-y-5 animate-fade-in">
              <Card>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Action</label>
                    <select
                      value={filters.action}
                      onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors"
                    >
                      <option value="">All actions</option>
                      {Object.keys(ACTION_LABELS).map(a => (
                        <option key={a} value={a}>{ACTION_LABELS[a]}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Handle"
                    placeholder="Filter by @handle"
                    value={filters.handle}
                    onChange={(e) => setFilters(prev => ({ ...prev, handle: e.target.value }))}
                  />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        label="Limit"
                        type="number"
                        value={filters.limit}
                        onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value) || 200 }))}
                      />
                    </div>
                    <Button onClick={fetchActivityLogs} disabled={activityLoading} className="mb-0.5">
                      {activityLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                    {activityLogs.length} events
                  </h3>
                </div>
                {activityLoading && activityLogs.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-brand" />
                  </div>
                ) : activityLogs.length === 0 ? (
                  <p className="text-center text-slate-400 py-12">No activity logs found</p>
                ) : (
                  <div className="overflow-x-auto -mx-6 px-6">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Action</th>
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Handle</th>
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">When</th>
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide hidden lg:table-cell">IP</th>
                          <th className="text-left py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide hidden xl:table-cell">Referrer</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {activityLogs.map((log, i) => (
                          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="py-2 pr-4"><ActionBadge action={log.action} /></td>
                            <td className="py-2 pr-4 font-mono text-xs text-slate-700 dark:text-slate-300">@{log.handle}</td>
                            <td className="py-2 pr-4 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatTime(log.timestamp)}</td>
                            <td className="py-2 pr-4 font-mono text-xs text-slate-400 hidden lg:table-cell">{log.ipAddress || '—'}</td>
                            <td className="py-2 text-xs text-slate-400 max-w-[200px] truncate hidden xl:table-cell">{log.referrer || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── Messages Tab ── */}
          {activeTab === 'messages' && (
            <div className="space-y-5 animate-fade-in">
              <Card>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Input
                    label="Handle"
                    placeholder="Filter by @handle"
                    value={messageFilters.handle}
                    onChange={(e) => setMessageFilters(prev => ({ ...prev, handle: e.target.value }))}
                  />
                  <Input
                    label="Search content"
                    placeholder="Search message text"
                    value={messageFilters.search}
                    onChange={(e) => setMessageFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        label="Limit"
                        type="number"
                        value={messageFilters.limit}
                        onChange={(e) => setMessageFilters(prev => ({ ...prev, limit: parseInt(e.target.value) || 100 }))}
                      />
                    </div>
                    <Button onClick={fetchMessages} disabled={messagesLoading} className="mb-0.5">
                      {messagesLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">{messages.length} QR codes</h3>
                </div>

                {messagesLoading && messages.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-brand" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-slate-400 py-12">No messages found</p>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg, i) => (
                      <div key={i} className="flex gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">@{msg.handle}</span>
                            <Badge variant="default" className="text-xs">
                              {new Date(msg.updatedAt).toLocaleDateString()}
                            </Badge>
                            {msg.imageUrl && (
                              <Badge variant="info" className="text-xs">
                                <Image size={10} className="mr-0.5" />img
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{msg.message}</p>
                        </div>
                        <div className="shrink-0 flex items-start">
                          {deleteConfirm === msg.handle ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-slate-500 whitespace-nowrap">Delete?</span>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteMessage(msg.handle)}
                                disabled={deleting === msg.handle}
                              >
                                {deleting === msg.handle ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirm(null)}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(msg.handle)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── Config Tab ── */}
          {activeTab === 'config' && (
            <div className="space-y-6 animate-fade-in">
              {/* Image settings */}
              <Card>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">Image Settings</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfig({ maxImageFileSizeMB: 5, maxImageWidth: 1200, maxImageHeight: 1200, imageQuality: 0.8 })}
                      disabled={updatingConfig}
                    >
                      Defaults
                    </Button>
                    <Button variant="primary" size="sm" onClick={updateConfig} disabled={updatingConfig}>
                      {updatingConfig ? <><Loader2 size={14} className="mr-1.5 animate-spin" />Saving...</> : 'Save'}
                    </Button>
                  </div>
                </div>

                {configLoading ? (
                  <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-brand" /></div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Input
                      label="Max file size (MB)"
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="50"
                      value={config.maxImageFileSizeMB}
                      onChange={(e) => setConfig(prev => ({ ...prev, maxImageFileSizeMB: parseFloat(e.target.value) || 5 }))}
                      hint="Maximum upload size"
                    />
                    <Input
                      label="Max width (px)"
                      type="number"
                      min="100"
                      max="4000"
                      value={config.maxImageWidth}
                      onChange={(e) => setConfig(prev => ({ ...prev, maxImageWidth: parseInt(e.target.value) || 1200 }))}
                    />
                    <Input
                      label="Max height (px)"
                      type="number"
                      min="100"
                      max="4000"
                      value={config.maxImageHeight}
                      onChange={(e) => setConfig(prev => ({ ...prev, maxImageHeight: parseInt(e.target.value) || 1200 }))}
                    />
                    <Input
                      label="Quality (0.1–1.0)"
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="1.0"
                      value={config.imageQuality}
                      onChange={(e) => setConfig(prev => ({ ...prev, imageQuality: parseFloat(e.target.value) || 0.8 }))}
                      hint="JPEG compression quality"
                    />
                  </div>
                )}
              </Card>

              {/* Password reset */}
              <Card>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">Force Password Reset</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Resets a user's password to the default value. Use with caution.
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="@handle"
                      value={resetPasswordHandle}
                      onChange={(e) => setResetPasswordHandle(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="danger"
                    onClick={handleResetPassword}
                    disabled={resettingPassword || !resetPasswordHandle.trim()}
                    className="shrink-0"
                  >
                    {resettingPassword ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
                  </Button>
                </div>
              </Card>
            </div>
          )}

        </div>
      </div>
    </>
  )
}

export default AdminNew
